import type { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import { useAppState } from '../../../AppStateProvider';
import { EmptyStateView } from '../../../components/EmptyStateView';
import { LoadingView } from '../../../components/LoadingView';
import type { Contact } from '../../../domain/contact';
import { useObserveIntersection } from '../../../hooks/useObserveIntersection';
import { ContactItem } from '../ContactItem';

import { StyledContactList, StyledLoader, StyledLoaderWrapper, StyledContactListBackground } from './styles';

export const ContactListInfinite = ({
  data,
  isFetchingNextPage,
  isLoading,
  isError,
  fetchNextPage,
  hasNextPage,
  isFetching,
  shouldAnimatePrivateDataMount,
}: UseInfiniteQueryResult<InfiniteData<Contact[]>> & {
  shouldAnimatePrivateDataMount: boolean;
}) => {
  const { contactFilters } = useAppState();
  const contacts = data?.pages.flat() || [];
  const hasContacts = !!contacts.length;

  const getSentinelElementSelector = () => {
    const contactId = contacts[contacts.length - 3]?.id ?? '';
    return `[data-contact-id="${contactId}"]`;
  };

  useObserveIntersection({
    sentinelSelector: getSentinelElementSelector(),
    shouldObserve: hasContacts && !!hasNextPage && !isFetchingNextPage,
    onIntersection: fetchNextPage as () => void,
  });

  if (isLoading || (isFetching && !isFetchingNextPage)) {
    return <LoadingView />;
  }

  if (!hasContacts || isError) {
    return (
      <EmptyStateView
        variant="lens-small"
        img="/images/empty-state-lens.svg"
        textKey={contactFilters.searchText ? 'contacts.search.empty_state.title' : undefined}
        messageKey={contactFilters.searchText ? 'contacts.search.empty_state.subtitle' : 'contacts.empty_state'}
      />
    );
  }

  return (
    <StyledContactList>
      <StyledContactListBackground data-automation="ContactListInfinite">
        {contacts.map((contact) => (
          <ContactItem
            key={contact.id || contact.name}
            contact={contact}
            shouldAnimatePrivateDataMount={shouldAnimatePrivateDataMount}
          />
        ))}
        {isFetchingNextPage ? (
          <StyledLoaderWrapper>
            <StyledLoader />
          </StyledLoaderWrapper>
        ) : null}
      </StyledContactListBackground>
    </StyledContactList>
  );
};
